import type { ComponentType } from '../../../types/builder.types'
import { KPICard } from '../../visualizations/KPICard'
import { TimeSeriesChart } from '../../visualizations/TimeSeriesChart'
import { BarChartComponent } from '../../visualizations/BarChartComponent'
import { DataTable } from '../../visualizations/DataTable'
// Fase 1: Componentes críticos
import { MapComponent } from '../../visualizations/MapComponent'
import { PieChartComponent } from '../../visualizations/PieChartComponent'
import { GaugeComponent } from '../../visualizations/GaugeComponent'
// Fase 2: Componentes muy útiles
import { AreaChartComponent } from '../../visualizations/AreaChartComponent'
import { StackedBarComponent } from '../../visualizations/StackedBarComponent'
import { ScatterPlotComponent } from '../../visualizations/ScatterPlotComponent'
// Fase 3: Nice to have
import { HeatmapComponent } from '../../visualizations/HeatmapComponent'
import { TreemapComponent } from '../../visualizations/TreemapComponent'
import { RadarChartComponent } from '../../visualizations/RadarChartComponent'
import { RadialBarComponent } from '../../visualizations/RadialBarComponent'

interface ComponentPreviewProps {
  component: {
    id: string
    name: string
    type: ComponentType
    configuration?: any
  }
}

// Datos de ejemplo para previews
const mockKPIData = 123456

const mockChartData = [
  { fecha: '2020', valor: 1200 },
  { fecha: '2021', valor: 1800 },
  { fecha: '2022', valor: 1500 },
  { fecha: '2023', valor: 2100 },
  { fecha: '2024', valor: 2400 },
]

const mockTableData = [
  { nombre: 'Monte de Utilidad Pública', tipo: 'AL', superficie: 1234.56 },
  { nombre: 'Zona Protegida', tipo: 'ZP', superficie: 2345.67 },
  { nombre: 'Área Recreativa', tipo: 'AR', superficie: 789.12 },
]

const mockTableColumns = [
  { key: 'nombre', label: 'Nombre', sortable: true },
  { key: 'tipo', label: 'Tipo', sortable: true },
  { key: 'superficie', label: 'Superficie (ha)', sortable: true },
]

// Mock data para nuevos componentes
const mockMapData = [
  { nombre: 'Valladolid', lat: 41.6523, lon: -4.7245, latitud: 41.6523, longitud: -4.7245, poblacion: 298866 },
  { nombre: 'Burgos', lat: 42.3439, lon: -3.6969, latitud: 42.3439, longitud: -3.6969, poblacion: 175821 },
  { nombre: 'Salamanca', lat: 40.9701, lon: -5.6635, latitud: 40.9701, longitud: -5.6635, poblacion: 143978 },
]

const mockPieData = [
  { name: 'Pino silvestre', value: 35 },
  { name: 'Encina', value: 25 },
  { name: 'Roble', value: 20 },
  { name: 'Haya', value: 15 },
  { name: 'Otros', value: 5 },
]

const mockGaugeData = { value: 68 }

const mockMultiSeriesData = [
  { categoria: 'Ene', serie1: 120, serie2: 80, serie3: 95 },
  { categoria: 'Feb', serie1: 140, serie2: 90, serie3: 105 },
  { categoria: 'Mar', serie1: 160, serie2: 100, serie3: 115 },
  { categoria: 'Abr', serie1: 180, serie2: 110, serie3: 125 },
]

const mockScatterData = [
  { x: 10, y: 20, z: 5, group: 'A' },
  { x: 15, y: 25, z: 8, group: 'A' },
  { x: 20, y: 30, z: 12, group: 'B' },
  { x: 25, y: 35, z: 15, group: 'B' },
  { x: 30, y: 40, z: 18, group: 'C' },
]

const mockHeatmapData = [
  { x: 'Lun', y: '8:00', value: 45 },
  { x: 'Lun', y: '12:00', value: 78 },
  { x: 'Mar', y: '8:00', value: 52 },
  { x: 'Mar', y: '12:00', value: 85 },
  { x: 'Mie', y: '8:00', value: 60 },
  { x: 'Mie', y: '12:00', value: 92 },
]

const mockTreemapData = [
  { name: 'Bosques', value: 450 },
  { name: 'Pastizales', value: 320 },
  { name: 'Cultivos', value: 280 },
  { name: 'Urbano', value: 150 },
]

const mockRadarData = [
  { subject: 'Biodiversidad', value1: 80, value2: 65 },
  { subject: 'Carbono', value1: 90, value2: 75 },
  { subject: 'Agua', value1: 70, value2: 85 },
  { subject: 'Recreación', value1: 60, value2: 70 },
  { subject: 'Madera', value1: 85, value2: 80 },
]

const mockRadialBarData = [
  { name: 'Ávila', value: 75 },
  { name: 'Burgos', value: 85 },
  { name: 'León', value: 65 },
  { name: 'Palencia', value: 55 },
]

export function ComponentPreview({ component }: ComponentPreviewProps) {
  const config = component.configuration || {}

  

  switch (component.type) {
    case 'kpi':
      return (
        <KPICard
          title={config.metadata?.title || component.name}
          value={mockKPIData}
          unit={config.styling?.unit || 'ha'}
          color={config.styling?.color || 'green'}
          icon={config.styling?.icon}
        />
      )

    case 'line-chart':
      return (
        <TimeSeriesChart
          title={config.metadata?.title || component.name}
          data={mockChartData}
          dataKey={config.chartConfig?.yAxisKey || 'valor'}
          xAxisKey={config.chartConfig?.xAxisKey || 'fecha'}
          color={config.chartConfig?.chartColor || '#10b981'}
        />
      )

    case 'bar-chart': {
      const barAliases = config.customQuery?.config?.fieldAliases as
        | Record<string, string>
        | undefined
      const resolveBarKey = (k: string) => (barAliases && barAliases[k]) || k
      return (
        <BarChartComponent
          title={config.metadata?.title || component.name}
          data={mockChartData}
          xKey={resolveBarKey(config.chartConfig?.xAxisKey || 'fecha')}
          yKey={resolveBarKey(config.chartConfig?.yAxisKey || 'valor')}
          color={config.chartConfig?.chartColor || '#3b82f6'}
        />
      )
    }

    case 'table':
      // Use configured columns if available, otherwise use mock columns
      const tableColumns = config.tableConfig?.columns?.length > 0
        ? config.tableConfig.columns.map((col: any) => ({
            key: col.key,
            label: col.label,
            sortable: col.sortable,
          }))
        : mockTableColumns

      return (
        <DataTable
          title={config.metadata?.title || component.name}
          data={mockTableData}
          columns={tableColumns}
          pageSize={config.tableConfig?.pageSize || 10}
          loading={false}
        />
      )

    // =====================================================
    // FASE 1: COMPONENTES CRÍTICOS
    // =====================================================

    case 'map':
      return (
        <MapComponent
          title={config.metadata?.title || component.name}
          data={mockMapData}
          mapType={config.mapConfig?.mapType || 'markers'}
          centerLat={config.mapConfig?.centerLat || 41.6488}
          centerLon={config.mapConfig?.centerLon || -4.7236}
          zoom={config.mapConfig?.zoom || 7}
        />
      )

    case 'pie-chart':
      return (
        <PieChartComponent
          title={config.metadata?.title || component.name}
          data={mockPieData}
          dataKey={config.pieConfig?.dataKey || 'value'}
          nameKey={config.pieConfig?.nameKey || 'name'}
          showPercentage={config.pieConfig?.showPercentage}
          innerRadius={config.pieConfig?.innerRadius}
          colors={config.pieConfig?.colors}
        />
      )

    case 'gauge':
      return (
        <GaugeComponent
          title={config.metadata?.title || component.name}
          data={mockGaugeData}
          valueKey={config.gaugeConfig?.valueKey || 'value'}
          min={config.gaugeConfig?.min || 0}
          max={config.gaugeConfig?.max || 100}
          unit={config.gaugeConfig?.unit || '%'}
          thresholds={config.gaugeConfig?.thresholds}
        />
      )

    // =====================================================
    // FASE 2: COMPONENTES MUY ÚTILES
    // =====================================================

    case 'area-chart':
      return (
        <AreaChartComponent
          title={config.metadata?.title || component.name}
          data={mockMultiSeriesData}
          xAxisKey={config.areaChartConfig?.xAxisKey || 'categoria'}
          yAxisKeys={config.areaChartConfig?.yAxisKeys || ['serie1', 'serie2']}
          stacked={config.areaChartConfig?.stacked}
          areaType={config.areaChartConfig?.areaType}
          showGrid={config.areaChartConfig?.showGrid}
          colors={config.areaChartConfig?.colors}
        />
      )

    case 'stacked-bar':
      return (
        <StackedBarComponent
          title={config.metadata?.title || component.name}
          data={mockMultiSeriesData}
          xAxisKey={config.stackedBarConfig?.xAxisKey || 'categoria'}
          yAxisKeys={config.stackedBarConfig?.yAxisKeys || ['serie1', 'serie2', 'serie3']}
          layout={config.stackedBarConfig?.layout}
          showGrid={config.stackedBarConfig?.showGrid}
          colors={config.stackedBarConfig?.colors}
        />
      )

    case 'scatter-plot':
      return (
        <ScatterPlotComponent
          title={config.metadata?.title || component.name}
          data={mockScatterData}
          xAxisKey={config.scatterPlotConfig?.xAxisKey || 'x'}
          yAxisKey={config.scatterPlotConfig?.yAxisKey || 'y'}
          zAxisKey={config.scatterPlotConfig?.zAxisKey || 'z'}
          groupKey={config.scatterPlotConfig?.groupKey || 'group'}
          showGrid={config.scatterPlotConfig?.showGrid}
          colors={config.scatterPlotConfig?.colors}
        />
      )

    // =====================================================
    // FASE 3: NICE TO HAVE
    // =====================================================

    case 'heatmap':
      return (
        <HeatmapComponent
          title={config.metadata?.title || component.name}
          data={mockHeatmapData}
          xAxisKey={config.heatmapConfig?.xAxisKey || 'x'}
          yAxisKey={config.heatmapConfig?.yAxisKey || 'y'}
          valueKey={config.heatmapConfig?.valueKey || 'value'}
          colorScale={config.heatmapConfig?.colorScale}
          showValues={config.heatmapConfig?.showValues}
        />
      )

    case 'treemap':
      return (
        <TreemapComponent
          title={config.metadata?.title || component.name}
          data={mockTreemapData}
          nameKey={config.treemapConfig?.nameKey || 'name'}
          valueKey={config.treemapConfig?.valueKey || 'value'}
          colors={config.treemapConfig?.colors}
        />
      )

    case 'radar-chart':
      return (
        <RadarChartComponent
          title={config.metadata?.title || component.name}
          data={mockRadarData}
          subjectKey={config.radarChartConfig?.subjectKey || 'subject'}
          valueKeys={config.radarChartConfig?.valueKeys || ['value1', 'value2']}
          colors={config.radarChartConfig?.colors}
          fillOpacity={config.radarChartConfig?.fillOpacity}
        />
      )

    case 'radial-bar':
      return (
        <RadialBarComponent
          title={config.metadata?.title || component.name}
          data={mockRadialBarData}
          nameKey={config.radialBarConfig?.nameKey || 'name'}
          valueKey={config.radialBarConfig?.valueKey || 'value'}
          maxValue={config.radialBarConfig?.maxValue || 100}
          colors={config.radialBarConfig?.colors}
        />
      )

    default:
      return (
        <div className="bg-white border rounded-lg p-6 text-center text-gray-500">
          <div className="text-4xl mb-2">❓</div>
          <p className="text-sm">Tipo desconocido: {component.type}</p>
        </div>
      )
  }
}
